import { getTranslation } from '../../helpers';
import { openModal } from '../../helpers/modals';

const selectors = {
  bisTrigger: '[data-product-bis-trigger]',
  bisEmail: '[data-product-bis-email]',
  bisSize: '[data-product-bis-size]',
  bisSizeWrapper: '[data-product-bis-size-wrapper]',
  bisSubmit: '[data-product-bis-submit]',
  bisErrorMsg: '[data-product-bis-error-msg]',
};

// Stoq's public, unauthenticated back-in-stock signup endpoint. Auth is the
// shop domain header, not a secret key, so it's safe to call from the
// storefront (same pattern as the Klaviyo company_id call below).
const STOQ_API_URL = 'https://app.stoqapp.com/api/v1/intents.json';

/**
 * Product back-in-stock modal + waitlist submission.
 *
 * The provider is chosen by the `bis_provider` theme setting, surfaced as
 * window.bisProvider in snippets/dev-scripts.liquid. 'stoq' posts to Stoq's
 * intents API; anything else falls back to Klaviyo.
 */
export default class ProductBackInStock {
  constructor(formElement) {
    this.formElement = formElement;
    this.bisTrigger = this.formElement?.querySelector(selectors.bisTrigger);

    if (!this.bisTrigger) {
      return;
    }

    this.provider = window.bisProvider === 'stoq' ? 'stoq' : 'klaviyo';

    this._init();
  }

  _init() {
    this.bisTrigger.addEventListener('click', e => {
      e.preventDefault();
      const opened = openModal('product-bis-modal');
      if (!opened) {
        return;
      }
      this._bindBackInStockSubmission();
    });
  }

  _bindBackInStockSubmission() {
    const modal = document.getElementById('product-bis-modal');
    if (!modal) {
      return;
    }

    const bisEmail = modal.querySelector(selectors.bisEmail);
    const bisSubmit = modal.querySelector(selectors.bisSubmit);
    const bisErrorMsg = modal.querySelector(selectors.bisErrorMsg);
    const variantId = this.bisTrigger?.dataset.productBisVariantId;

    if (!bisEmail || !bisSubmit || !bisErrorMsg || !variantId) {
      return;
    }

    // Klaviyo's layout has no size dropdown, so this is a no-op there.
    const bisSize = this._populateSizes(modal, variantId);

    const updateMessage = (message, isError = true) => {
      bisErrorMsg.textContent = message;
      bisErrorMsg.classList.remove('hidden');
      bisErrorMsg.classList.toggle('text-lof-red', isError);
      bisErrorMsg.classList.toggle('text-black', !isError);
    };

    const clearMessage = () => {
      bisErrorMsg.textContent = '';
      bisErrorMsg.classList.add('hidden');
      bisErrorMsg.classList.add('text-lof-red');
      bisErrorMsg.classList.remove('text-black');
    };

    const handleSubmit = async e => {
      e.preventDefault();
      const email = bisEmail.value.trim();

      if (!this._isValidEmail(email)) {
        updateMessage(getTranslation('products.waitlist.email_error'));
        return;
      }

      // If the shopper switched sizes in the dropdown, submit that size
      // instead of the one they originally clicked the trigger on.
      const selectedVariantId =
        bisSize && bisSize.value ? bisSize.value : variantId;

      bisSubmit.setAttribute('disabled', 'disabled');
      try {
        const result =
          this.provider === 'stoq'
            ? await this._submitStoq(email, selectedVariantId)
            : await this._submitKlaviyo(email, selectedVariantId);

        updateMessage(
          getTranslation(`products.waitlist.${result}`),
          result !== 'success',
        );
      } catch (error) {
        console.error(error);
        updateMessage(getTranslation('products.waitlist.error'));
      } finally {
        bisSubmit.removeAttribute('disabled');
      }
    };

    bisSubmit.onclick = handleSubmit;
    bisEmail.oninput = clearMessage;
  }

  /**
   * Fills the modal's size dropdown from the trigger's sold-out variant
   * payload and pre-selects the size the shopper clicked "Notify me" on.
   *
   * The modal is rendered once globally, so it can't be product-scoped at
   * render time — a quickshop opened from a PDP would otherwise show the
   * PDP product's sizes. Hidden unless there's more than one size to pick.
   *
   * @param {HTMLElement} modal - The back-in-stock modal element
   * @param {string} variantId - Variant id from the trigger
   * @returns {HTMLSelectElement|null} - The select, or null when not shown
   */
  _populateSizes(modal, variantId) {
    const bisSize = modal.querySelector(selectors.bisSize);
    const bisSizeWrapper = modal.querySelector(selectors.bisSizeWrapper);

    if (!bisSize || !bisSizeWrapper) {
      return null;
    }

    let variants = [];
    try {
      variants = JSON.parse(
        this.bisTrigger?.dataset.productBisVariants || '[]',
      );
    } catch (error) {
      variants = [];
    }

    bisSize.innerHTML = '';
    variants.forEach(variant => {
      const option = document.createElement('option');
      option.value = variant.id;
      option.textContent = variant.title;
      bisSize.appendChild(option);
    });

    if (variants.length < 2) {
      bisSizeWrapper.classList.add('hidden');
      return null;
    }

    bisSizeWrapper.classList.remove('hidden');
    bisSize.value = variantId;
    return bisSize;
  }

  /**
   * @param {string} email - Shopper's email
   * @param {string} variantId - Variant to subscribe to
   * @returns {Promise<string>} - Translation key suffix under products.waitlist
   */
  async _submitStoq(email, variantId) {
    const shopDomain = window.Shopify?.shop || '';
    const productId = this.bisTrigger?.dataset.productBisProductId;

    if (!shopDomain || !productId) {
      return 'error';
    }

    // Stoq's own widget reads the shopper's market off the page
    // automatically. Per Stoq support, a custom integration has to grab it
    // and pass it explicitly or market-based alert filtering won't apply.
    // Stoq's app-embed script sets this global on page load.
    const marketId = window._RestockRocketConfig?.marketId;

    const intent = {
      shopify_variant_id: Number(variantId),
      shopify_product_id: Number(productId),
      channel: 'email',
      quantity: 1,
      source: 'api',
    };
    const customer = {
      email,
      accepts_marketing: true,
    };

    if (marketId) {
      intent.shopify_market_id = marketId;
      customer.shopify_market_id = marketId;
    }

    const bisResponse = await fetch(STOQ_API_URL, {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Shopify-Shop-Domain': shopDomain,
      },
      body: JSON.stringify({ intent, customer }),
    });

    // A duplicate signup (same email + variant already has a pending intent)
    // comes back as a 200 with a body of {"errors":{"already_registered":true}}
    // — NOT a non-ok status — so this has to be checked before the response.ok
    // check below, or it silently falls through to the success message.
    let bisJson = null;
    try {
      bisJson = await bisResponse.json();
    } catch (parseError) {
      bisJson = null;
    }

    if (bisJson?.errors?.already_registered) {
      return 'already_subscribed';
    }

    if (!bisResponse.ok) {
      return 'error';
    }

    return 'success';
  }

  /**
   * @param {string} email - Shopper's email
   * @param {string} variantId - Variant to subscribe to
   * @returns {Promise<string>} - Translation key suffix under products.waitlist
   */
  async _submitKlaviyo(email, variantId) {
    const companyId = window.klaviyoApiKey || '';
    if (!companyId) {
      return 'error';
    }

    const listId = window.klaviyoListId || '';

    const bisResponse = await fetch(
      `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${companyId}`,
      {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
          revision: '2024-05-15',
        },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email,
                  },
                },
              },
              channels: ['EMAIL'],
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: `$shopify:::$default:::${variantId}`,
                },
              },
            },
          },
        }),
      },
    );

    if (!bisResponse.ok) {
      return 'error';
    }

    if (listId) {
      await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${companyId}`,
        {
          method: 'POST',
          headers: {
            revision: '2024-07-15',
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      properties: {
                        SubscribeSource: 'Back In Stock Popup',
                      },
                      email,
                    },
                  },
                },
              },
              relationships: {
                list: { data: { type: 'list', id: listId } },
              },
            },
          }),
        },
      );
    }

    return 'success';
  }

  _isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }
}
